// src/utils/loadTeam.js
//
// Which team a load's work belongs to, and how a viewer relates to that team.
//
// A load is the work of one team. Who may change its dispatcher, which Main
// Admin sees it, and whether a dispatcher sees it as their own or as greyed
// reference all hang off that one answer, so it is decided here and nowhere
// else.
//
// The team of a load, in order:
//   1. load.teamId — stamped when the load is created (and when a driver from
//      another team is put on it). Stable: moving people later does not move
//      the work they already did.
//   2. Its dispatcher's team. Loads created before stamping existed have no
//      teamId; whoever dispatches them is the best evidence of whose they are.
//      This is also what keeps a transferred driver's old loads with the team
//      that booked them — the driver moved, the dispatcher did not.
//   3. Its driver's team, then its creator's team.
//
// Team ids are made as `${companyId}_team_${random}` (see teams.js), so the
// company a team belongs to can be read off the id itself.

export const companyOfTeam = (teamId) =>
  (typeof teamId === 'string' && teamId.includes('_team_') ? teamId.split('_team_')[0] : null);

// A team only counts when its company is on the user's company list — the
// same rule as getUserTeamIds in teams.js. A leftover team from a company move
// must not pull a load onto a team the user is no longer part of.
const teamsOf = (user) => {
  const ids = Array.isArray(user?.teamIds) ? user.teamIds.filter(Boolean) : [];
  const companies = user?.assignedParentCompanyIds;
  if (!Array.isArray(companies)) return ids;
  return ids.filter(t => { const c = companyOfTeam(t); return !c || companies.includes(c); });
};

/** A user's team in this company if they have one there, else their first. */
function pickTeam(teamIds, companyId) {
  if (!teamIds.length) return null;
  return (companyId && teamIds.find(t => companyOfTeam(t) === companyId)) || teamIds[0];
}

/** The team to stamp on a load this user is creating. */
export function teamForNewLoad(creator, companyId) {
  return pickTeam(teamsOf(creator), companyId);
}

// A driver's team counts only when it is a team of the driver's own company.
export const driverOwnTeam = (driver) =>
  (driver?.teamId && companyOfTeam(driver.teamId) === driver.parentCompanyId ? driver.teamId : null);

/**
 * The teams a user was on when a load was booked. formerTeams records when they
 * left each team, so a team they left AFTER the load was booked is one they
 * were on at the time — the earliest such departure first. With none, they
 * have not moved since, and their teams now are the answer.
 *
 * Without this, moving a dispatcher to another team carried every old load of
 * theirs that has no teamId along with them: the old team lost it (and its
 * Accounting history), and the Super Admin's "See only" / "No access" answer
 * did not apply to it, because it looked like their new team's work.
 */
function teamsAt(user, at) {
  if (!user || at == null) return teamsOf(user);
  const leftSince = getFormerTeams(user)
    .map(f => ({ teamId: f.teamId, at: toMillis(f.removedAt) }))
    .filter(f => f.at != null && f.at > at)
    .sort((a, b) => a.at - b.at)
    .map(f => f.teamId);
  return leftSince.length > 0 ? leftSince : teamsOf(user);
}

/** The same for a driver: the team they were on when the load was booked. */
function driverTeamAt(driver, at) {
  if (driver && at != null) {
    const leftSince = getFormerTeams(driver)
      .map(f => ({ teamId: f.teamId, at: toMillis(f.removedAt) }))
      .filter(f => f.at != null && f.at > at)
      .sort((a, b) => a.at - b.at);
    if (leftSince.length > 0) return leftSince[0].teamId;
  }
  return driverOwnTeam(driver);
}

export function deriveLoadTeamId(load, usersById, driversById) {
  if (!load) return null;
  if (load.teamId) return load.teamId;
  const companyId = load.parentCompanyId || null;
  const bookedAt = toMillis(load.createdAt);
  return pickTeam(teamsAt(usersById.get(load.dispatcherId), bookedAt), companyId) ||
    driverTeamAt(driversById.get(load.driverId), bookedAt) ||
    pickTeam(teamsAt(usersById.get(load.createdBy), bookedAt), companyId) ||
    null;
}

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Teams this user was taken off, and when. Written by useTeamActions whenever
 * someone leaves a team. Lets a replaced Main Admin finish the work they
 * already had without also seeing everything their successor books.
 */
export function getFormerTeams(user) {
  const list = Array.isArray(user?.formerTeams) ? user.formerTeams : [];
  return list.filter(f => f && f.teamId);
}

/**
 * What a removed person may still do with their own unfinished loads on a team
 * they left, as the Super Admin answered when removing them:
 *   'manage' — see and change them;  'view' — see only;  'none' — nothing.
 * Removals recorded before the question existed count as 'view'.
 */
export const LEFT_TEAM_ACCESS = ['manage', 'view', 'none'];

/** What a viewer's team membership says about what they work on. */
export function getTeamAccess(user) {
  const teams = teamsOf(user);
  const former = new Map();
  const formerAccess = new Map();
  const formerAsMainAdmin = new Map();
  for (const f of getFormerTeams(user)) {
    if (teams.includes(f.teamId)) continue;           // back on that team now
    const at = toMillis(f.removedAt);
    if (at == null) continue;
    // Keep the latest removal if they left the same team more than once.
    if (!former.has(f.teamId) || former.get(f.teamId) < at) {
      former.set(f.teamId, at);
      formerAccess.set(f.teamId, LEFT_TEAM_ACCESS.includes(f.loadAccess) ? f.loadAccess : 'view');
      // Removals recorded before this was written: unknown (undefined).
      formerAsMainAdmin.set(f.teamId, typeof f.asMainAdmin === 'boolean' ? f.asMainAdmin : undefined);
    }
  }
  return { teams, former, formerAccess, formerAsMainAdmin, usesTeams: teams.length > 0 || former.size > 0 };
}

/**
 * How a load relates to a viewer's teams.
 *   'own'    — their team's work
 *   'former' — work of a team they have left, booked before they left
 *   'none'   — the load belongs to no team (companies not using teams)
 *   'other'  — somebody else's team
 */
export function loadTeamRelation(load, teamKey, access) {
  if (!teamKey) return 'none';
  if (access.teams.includes(teamKey)) return 'own';
  const leftAt = access.former.get(teamKey);
  if (leftAt != null) {
    const created = toMillis(load.createdAt);
    // No creation time on record: older than any removal, so it is old work.
    if (created == null || created < leftAt) return 'former';
  }
  return 'other';
}
